import {create} from 'zustand'
import {useShallow} from 'zustand/react/shallow'
import {immer} from 'zustand/middleware/immer'
import {persist, createJSONStorage} from 'zustand/middleware'
import {createTtlStorage} from '../utils/ttlStorage'
import dayjs from '../utils/localDayjs'
import {EShift} from '../types/app.type'
import type {TReportOutcome} from '../types/shiftReport.type'
import type {TZoneKind} from '../types/protectionZone.types'
import {
  getMondayOfWeek,
  getWeekAndYearFromDate,
  isCurrentWeek,
} from '../utils/dates'
import {Axis} from '../utils/axis'
import {DEFAULT_ZONE_KIND} from '../utils/zoneKind.functions'
import {TWindowOverride} from '../utils/sectorWindow.functions'
import type {TDayActivityPicks} from '../utils/synopticDayActivities.functions'
import {theme} from '../theme'

interface UIState {
  viewMode: 'week' | 'day'
  selectedDate: string
  currentShift: EShift
  selectedTrack: string | 'all'
  showZEP: boolean
  teamIdsVisible: string[]
  statusesVisible: string[]
  onlyActiveZones: boolean
  /** Which protection zones the drawings show: all, simple (PK-drawn) or groups. One state for frise and ZEP lanes. */
  zoneKindFilter: TZoneKind
  selectedTrackIds: string[]
  workTrackIds: string[]
  workTrackProjectId: string | null
  selectedSectorIds: string[]
  /**
   * Manual PK window of the linear view (work/network). One of the two ways to
   * designate the window; the other is the sector filter (`selectedSectorIds`).
   * Lives in the store so the controls can render in the global header (above the
   * data wrapper that consumes it). `granularity` is the ruler step, orthogonal
   * to the bounds.
   */
  linearWindowOverride: TWindowOverride
  /**
   * The synoptic's toolbar picks, one set for the settings preview and the
   * synoptic view: the plan reads the same on both. Undefined type = the first
   * type the zones use; undefined step = the synoptic's own opening step.
   */
  synopticTypeId: string | undefined
  synopticZoneKind: TZoneKind
  synopticGranularity: number | undefined
  synopticShowPk: boolean
  /** The synoptic's own track pick, apart from the linear views' `workTrackIds`. */
  synopticTrackIds: string[]
  synopticTrackNetworkId: string | null
  selectedProtectionModeIds: string[]
  selectedActivityTypeIds: string[]
  entryFilter: string
  selectedRequestorIds: string[]
  selectedHolderIds: string[]
  selectedReportOutcomes: TReportOutcome[]
  reportTodoOnly: boolean
  selectedResourceRoleIds: string[]
  selectedResourceLocations: string[]
  listDateMode: 'cursor' | 'period'
  listPeriod: {from: string; to: string} | null
  axis: Axis
  setSelectedTrackIds: (ids: string[]) => void
  setWorkTrackIds: (ids: string[]) => void
  setWorkTrackProjectId: (id: string | null) => void
  setSelectedSectorIds: (ids: string[]) => void
  setLinearWindowBounds: (bounds: {pkStart?: number; pkEnd?: number}) => void
  setLinearWindowGranularity: (granularity: number) => void
  clearLinearWindowBounds: () => void
  setSynopticTypeId: (id: string) => void
  setSynopticZoneKind: (kind: TZoneKind) => void
  setSynopticGranularity: (granularity: number) => void
  toggleSynopticShowPk: () => void
  setSynopticTrackIds: (ids: string[]) => void
  setSynopticTrackNetworkId: (id: string | null) => void
  setSelectedProtectionModeIds: (ids: string[]) => void
  setSelectedActivityTypeIds: (ids: string[]) => void
  setEntryFilter: (value: string) => void
  setSelectedRequestorIds: (ids: string[]) => void
  setSelectedHolderIds: (ids: string[]) => void
  setSelectedReportOutcomes: (outcomes: TReportOutcome[]) => void
  setReportTodoOnly: (value: boolean) => void
  setSelectedResourceRoleIds: (ids: string[]) => void
  setSelectedResourceLocations: (ids: string[]) => void
  setListDateMode: (mode: 'cursor' | 'period') => void
  setListPeriod: (range: {from: string; to: string} | null) => void
  clearAllFilters: () => void
  setViewMode: (mode: 'week' | 'day') => void
  setSelectedDate: (date: string) => void
  setWeekNavigation: (direction: 'prev' | 'next' | 'current') => void
  setDayNavigation: (direction: 'prev' | 'next' | 'current') => void
  setCurrentShift: (shift: EShift) => void
  setSelectedTrack: (voie: string | 'all') => void
  toggleShowZEP: () => void
  toggleTeamVisible: (teamId: string) => void
  setTeamIdsVisible: (ids: string[]) => void
  toggleStatusVisible: (status: string) => void
  setStatusesVisible: (statuses: string[]) => void
  setOnlyActiveZones: (value: boolean) => void
  setZoneKindFilter: (kind: TZoneKind) => void
  setAxis: (axis: Axis) => void
}

// Mobile users default to "both" shifts (the screen is hardwired to day view
// and they want to see everything at a glance); desktop defaults to "day".
// Evaluated once at module load. We reuse the theme breakpoint so the query
// stays in sync with useIsMobile(); matchMedia wants the query without the
// leading "@media " prefix that MUI prepends.
const mobileQuery = theme.breakpoints.down('sm').replace(/^@media\s*/, '')
const isMobileAtLoad =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(mobileQuery).matches

// Shared with dashboard.slice.ts so both persisted filter sets expire alike.
export const FILTERS_TTL_MS = 12 * 60 * 60 * 1000

const useUIStoreBase = create<UIState>()(
  persist(
    immer<UIState>(set => ({
      viewMode: 'week',
      selectedDate: dayjs().format('YYYY-MM-DD'), // Date du jour par défaut
      currentShift: isMobileAtLoad ? 'both' : 'day',
      selectedTrack: 'all',
      showZEP: true,
      teamIdsVisible: [],
      statusesVisible: [],
      onlyActiveZones: false,
      zoneKindFilter: DEFAULT_ZONE_KIND,
      selectedTrackIds: [],
      workTrackIds: [],
      workTrackProjectId: null,
      selectedSectorIds: [],
      linearWindowOverride: {},
      synopticTypeId: undefined,
      synopticZoneKind: 'all',
      synopticGranularity: undefined,
      synopticShowPk: true,
      synopticTrackIds: [],
      synopticTrackNetworkId: null,
      selectedProtectionModeIds: [],
      selectedActivityTypeIds: [],
      entryFilter: '',
      selectedRequestorIds: [],
      selectedHolderIds: [],
      selectedReportOutcomes: [],
      reportTodoOnly: false,
      selectedResourceRoleIds: [],
      selectedResourceLocations: [],
      listDateMode: 'cursor',
      listPeriod: null,
      axis: null as unknown as Axis,

      setViewMode: (mode: 'week' | 'day') => {
        set(state => {
          if (state.viewMode === mode) return
          state.viewMode = mode
          if (mode === 'day') {
            if (isCurrentWeek(state.selectedDate)) {
              state.selectedDate = dayjs().format('YYYY-MM-DD')
            } else {
              const {week, year} = getWeekAndYearFromDate(state.selectedDate)
              state.selectedDate = getMondayOfWeek(week, year)
            }
          }
          if (mode === 'week') {
            const {week, year} = getWeekAndYearFromDate(state.selectedDate)
            state.selectedDate = getMondayOfWeek(week, year)
          }
        })
      },

      setSelectedDate: (date: string) => {
        set(state => {
          state.selectedDate = date
        })
      },

      setWeekNavigation: (direction: 'prev' | 'next' | 'current') => {
        set(state => {
          if (direction === 'current') {
            state.selectedDate = dayjs().format('YYYY-MM-DD')
          } else if (direction === 'prev') {
            state.selectedDate = dayjs(state.selectedDate)
              .subtract(7, 'days')
              .format('YYYY-MM-DD')
          } else if (direction === 'next') {
            state.selectedDate = dayjs(state.selectedDate)
              .add(7, 'days')
              .format('YYYY-MM-DD')
          }
        })
      },
      setCurrentShift: (shift: EShift) => {
        set(state => {
          const wasShowingBoth = state.currentShift === 'both'
          state.currentShift = shift
          if (shift === 'both') {
            state.showZEP = false
          } else if (wasShowingBoth) {
            state.showZEP = true
          }
        })
      },

      setDayNavigation: (direction: 'prev' | 'next' | 'current') => {
        set(state => {
          if (direction === 'current') {
            state.selectedDate = dayjs().format('YYYY-MM-DD')
          } else if (direction === 'prev') {
            state.selectedDate = dayjs(state.selectedDate)
              .subtract(1, 'day')
              .format('YYYY-MM-DD')
          } else if (direction === 'next') {
            state.selectedDate = dayjs(state.selectedDate)
              .add(1, 'day')
              .format('YYYY-MM-DD')
          }
        })
      },

      setSelectedTrack: (voie: string | 'all') => {
        set(state => {
          state.selectedTrack = voie
        })
      },

      toggleShowZEP: () => {
        set(state => {
          state.showZEP = !state.showZEP
        })
      },

      toggleTeamVisible: (teamId: string) => {
        set(state => {
          if (state.teamIdsVisible.includes(teamId)) {
            state.teamIdsVisible = state.teamIdsVisible.filter(
              id => id !== teamId,
            )
          } else {
            state.teamIdsVisible.push(teamId)
          }
        })
      },

      setTeamIdsVisible: (ids: string[]) => {
        set({teamIdsVisible: ids})
      },

      toggleStatusVisible: (status: string) => {
        set(state => {
          if (state.statusesVisible.includes(status)) {
            state.statusesVisible = state.statusesVisible.filter(
              s => s !== status,
            )
          } else {
            state.statusesVisible.push(status)
          }
        })
      },

      setStatusesVisible: (statuses: string[]) => {
        set({statusesVisible: statuses})
      },

      setOnlyActiveZones: (value: boolean) => {
        set({onlyActiveZones: value})
      },

      setZoneKindFilter: (kind: TZoneKind) => {
        set(state => {
          state.zoneKindFilter = kind
        })
      },

      setSelectedTrackIds: (ids: string[]) => {
        set({selectedTrackIds: ids})
      },

      setWorkTrackIds: (ids: string[]) => {
        set({workTrackIds: ids})
      },

      setWorkTrackProjectId: (id: string | null) => {
        set({workTrackProjectId: id})
      },

      setSynopticTrackIds: (ids: string[]) => {
        set({synopticTrackIds: ids})
      },

      setSynopticTrackNetworkId: (id: string | null) => {
        set({synopticTrackNetworkId: id})
      },

      setSelectedSectorIds: (ids: string[]) => {
        set({selectedSectorIds: ids})
      },

      setLinearWindowBounds: (bounds: {pkStart?: number; pkEnd?: number}) => {
        set(state => {
          state.linearWindowOverride = {
            ...state.linearWindowOverride,
            ...bounds,
          }
        })
      },

      setLinearWindowGranularity: (granularity: number) => {
        set(state => {
          state.linearWindowOverride.granularity = granularity
        })
      },

      // Drop the typed PK bounds, keep the ruler step — a sector pick resets the
      // window afresh but display density is orthogonal.
      clearLinearWindowBounds: () => {
        set(state => {
          state.linearWindowOverride = state.linearWindowOverride.granularity
            ? {granularity: state.linearWindowOverride.granularity}
            : {}
        })
      },

      setSynopticTypeId: (id: string) => {
        set({synopticTypeId: id})
      },

      setSynopticZoneKind: (kind: TZoneKind) => {
        set({synopticZoneKind: kind})
      },

      setSynopticGranularity: (granularity: number) => {
        set({synopticGranularity: granularity})
      },

      toggleSynopticShowPk: () => {
        set(state => {
          state.synopticShowPk = !state.synopticShowPk
        })
      },

      setSelectedProtectionModeIds: (ids: string[]) => {
        set({selectedProtectionModeIds: ids})
      },

      setSelectedActivityTypeIds: (ids: string[]) => {
        set({selectedActivityTypeIds: ids})
      },

      setEntryFilter: (value: string) => {
        set({entryFilter: value})
      },

      setSelectedRequestorIds: (ids: string[]) => {
        set({selectedRequestorIds: ids})
      },

      setSelectedHolderIds: (ids: string[]) => {
        set({selectedHolderIds: ids})
      },

      setSelectedReportOutcomes: (outcomes: TReportOutcome[]) => {
        set({selectedReportOutcomes: outcomes})
      },

      setReportTodoOnly: (value: boolean) => {
        set({reportTodoOnly: value})
      },

      setSelectedResourceRoleIds: (ids: string[]) => {
        set({selectedResourceRoleIds: ids})
      },

      setSelectedResourceLocations: (ids: string[]) => {
        set({selectedResourceLocations: ids})
      },

      setListDateMode: (mode: 'cursor' | 'period') => {
        set({listDateMode: mode})
      },

      setListPeriod: (range: {from: string; to: string} | null) => {
        set(state => {
          state.listPeriod = range
          if (range) {
            // Cursor hand-off: the linear/work view (and other cursor screens)
            // cannot display a multi-week range, so pre-position the shared cursor
            // at the week containing the period's start. Landing on such a screen
            // then shows week view at the period start — a defined, announced slice
            // (see the period banner) rather than a silent truncation.
            const {week, year} = getWeekAndYearFromDate(range.from)
            state.selectedDate = getMondayOfWeek(week, year)
            state.viewMode = 'week'
          }
        })
      },

      // Reset the entire app-wide, cross-view filter set in one atomic update.
      // This clears filters wherever they were set — work views, protection
      // views, the resource calendar — not just the screen firing it. Navigation
      // (selectedDate/viewMode/currentShift, and the list tabs' date mode +
      // period) is intentionally left untouched: those are cursor state, not
      // filters — the period is the list's equivalent of the week/day cursor, so
      // clearing it would drop the user out of the view they chose. Screen-local
      // search inputs are not in the store; a screen calling this should reset
      // its own search too.
      clearAllFilters: () => {
        set(state => {
          state.selectedTrackIds = []
          state.workTrackIds = []
          // Null so the work filter re-defaults to the main tracks on next mount.
          state.workTrackProjectId = null
          state.selectedSectorIds = []
          state.linearWindowOverride = {}
          state.selectedProtectionModeIds = []
          state.selectedActivityTypeIds = []
          state.entryFilter = ''
          state.selectedRequestorIds = []
          state.selectedHolderIds = []
          state.selectedReportOutcomes = []
          state.reportTodoOnly = false
          state.selectedResourceRoleIds = []
          state.selectedResourceLocations = []
          state.teamIdsVisible = []
          state.statusesVisible = []
          state.selectedTrack = 'all'
          state.onlyActiveZones = false
          state.zoneKindFilter = DEFAULT_ZONE_KIND
          // The synoptic toolbar picks stay: they set what the plan draws, not
          // which activities are listed, so a reset from another view must not
          // redraw the plan.
        })
      },

      setAxis: (axis: Axis) => {
        set({axis})
      },
    })),
    {
      name: 'visualean-filters',
      // 12h TTL: a refresh / new tab the same day keeps the filters AND the
      // selected date; past 12h since the last change the whole set expires and
      // the store falls back to its defaults (today, unfiltered). This is what
      // makes persisting selectedDate safe — otherwise a stale tab would resume
      // on an old day.
      storage: createJSONStorage(() =>
        createTtlStorage(FILTERS_TTL_MS, localStorage),
      ),
      // Persist the shared filter set + the navigation context (date/shift/view).
      // selectedTrack/onlyActiveZones stay in-memory. statusesVisible is a fixed
      // set (pending/validated/deleted), never stale.
      // linearWindowOverride: only the ruler granularity (the "zoom") is kept —
      // it's a display preference. The pk bounds stay in-memory: they're tied to
      // a specific network/work and must not resume on an unrelated view.
      partialize: state => ({
        selectedTrackIds: state.selectedTrackIds,
        workTrackIds: state.workTrackIds,
        workTrackProjectId: state.workTrackProjectId,
        selectedSectorIds: state.selectedSectorIds,
        selectedProtectionModeIds: state.selectedProtectionModeIds,
        selectedActivityTypeIds: state.selectedActivityTypeIds,
        entryFilter: state.entryFilter,
        selectedRequestorIds: state.selectedRequestorIds,
        selectedHolderIds: state.selectedHolderIds,
        selectedReportOutcomes: state.selectedReportOutcomes,
        reportTodoOnly: state.reportTodoOnly,
        selectedResourceRoleIds: state.selectedResourceRoleIds,
        selectedResourceLocations: state.selectedResourceLocations,
        zoneKindFilter: state.zoneKindFilter,
        currentShift: state.currentShift,
        viewMode: state.viewMode,
        selectedDate: state.selectedDate,
        teamIdsVisible: state.teamIdsVisible,
        statusesVisible: state.statusesVisible,
        listDateMode: state.listDateMode,
        listPeriod: state.listPeriod,
        linearWindowOverride: {
          granularity: state.linearWindowOverride.granularity,
        },
        synopticTypeId: state.synopticTypeId,
        synopticZoneKind: state.synopticZoneKind,
        synopticGranularity: state.synopticGranularity,
        synopticShowPk: state.synopticShowPk,
        synopticTrackIds: state.synopticTrackIds,
        synopticTrackNetworkId: state.synopticTrackNetworkId,
      }),
      version: 1,
    },
  ),
)

// Hooks spécifiques pour sélecteurs granulaires
export const useUIStore = useUIStoreBase

export const useViewMode = () => useUIStore(state => state.viewMode)

export const useWeekNavigation = () => useUIStore(state => state.selectedDate)

export const useDayNavigation = () => useUIStore(state => state.selectedDate)

export const useCurrentShift = () => useUIStore(state => state.currentShift)

export const useSelectedTrack = () => useUIStore(state => state.selectedTrack)

export const useShowZEP = () => useUIStore(state => state.showZEP)

export const useTeamIdsVisible = () => useUIStore(state => state.teamIdsVisible)

export const useStatusesVisible = () =>
  useUIStore(state => state.statusesVisible)

export const useOnlyActiveZones = () =>
  useUIStore(state => state.onlyActiveZones)

export const useZoneKindFilter = () => useUIStore(state => state.zoneKindFilter)

export const useSelectedDate = () => useUIStore(state => state.selectedDate)

export const useAxis = () => useUIStore(state => state.axis)

// Persisted shared filters
export const useSelectedTrackIds = () =>
  useUIStore(state => state.selectedTrackIds)

export const useWorkTrackIds = () => useUIStore(state => state.workTrackIds)

export const useWorkTrackProjectId = () =>
  useUIStore(state => state.workTrackProjectId)

export const useSelectedSectorIds = () =>
  useUIStore(state => state.selectedSectorIds)

export const useLinearWindowOverride = () =>
  useUIStore(state => state.linearWindowOverride)

export const useSynopticTypeId = () => useUIStore(state => state.synopticTypeId)

export const useSynopticZoneKind = () =>
  useUIStore(state => state.synopticZoneKind)

export const useSynopticGranularity = () =>
  useUIStore(state => state.synopticGranularity)

export const useSynopticShowPk = () => useUIStore(state => state.synopticShowPk)

export const useSynopticTrackIds = () =>
  useUIStore(state => state.synopticTrackIds)

export const useSynopticTrackNetworkId = () =>
  useUIStore(state => state.synopticTrackNetworkId)

export const useSelectedProtectionModeIds = () =>
  useUIStore(state => state.selectedProtectionModeIds)

export const useSelectedActivityTypeIds = () =>
  useUIStore(state => state.selectedActivityTypeIds)

export const useEntryFilter = () => useUIStore(state => state.entryFilter)

export const useSelectedRequestorIds = () =>
  useUIStore(state => state.selectedRequestorIds)

export const useSelectedHolderIds = () =>
  useUIStore(state => state.selectedHolderIds)

export const useSelectedReportOutcomes = () =>
  useUIStore(state => state.selectedReportOutcomes)

export const useReportTodoOnly = () => useUIStore(state => state.reportTodoOnly)

export const useSelectedResourceRoleIds = () =>
  useUIStore(state => state.selectedResourceRoleIds)

export const useSelectedResourceLocations = () =>
  useUIStore(state => state.selectedResourceLocations)

// List period (date-range) view — shared by the activities and notices tabs
export const useListDateMode = () => useUIStore(state => state.listDateMode)

export const useListPeriod = () => useUIStore(state => state.listPeriod)

export const useDayActivityPicks = (): TDayActivityPicks =>
  useUIStore(
    useShallow(state => ({
      date: state.selectedDate,
      shift: state.currentShift,
      trackIds: state.selectedTrackIds,
      activityTypes: state.selectedActivityTypeIds,
      teamIds: state.teamIdsVisible,
      requestorIds: state.selectedRequestorIds,
      protectionModeIds: state.selectedProtectionModeIds,
      statuses: state.statusesVisible,
    })),
  )

// Actions exportées pour usage direct
export const useUIActions = () =>
  useUIStore(
    useShallow(state => ({
      setViewMode: state.setViewMode,
      setSelectedDate: state.setSelectedDate,
      setCurrentShift: state.setCurrentShift,
      setSelectedTrack: state.setSelectedTrack,
      toggleShowZEP: state.toggleShowZEP,
      toggleTeamVisible: state.toggleTeamVisible,
      setTeamIdsVisible: state.setTeamIdsVisible,
      toggleStatusVisible: state.toggleStatusVisible,
      setStatusesVisible: state.setStatusesVisible,
      setOnlyActiveZones: state.setOnlyActiveZones,
      setZoneKindFilter: state.setZoneKindFilter,
      setWeekNavigation: state.setWeekNavigation,
      setDayNavigation: state.setDayNavigation,
      setAxis: state.setAxis,
      setSelectedTrackIds: state.setSelectedTrackIds,
      setWorkTrackIds: state.setWorkTrackIds,
      setWorkTrackProjectId: state.setWorkTrackProjectId,
      setSelectedSectorIds: state.setSelectedSectorIds,
      setLinearWindowBounds: state.setLinearWindowBounds,
      setLinearWindowGranularity: state.setLinearWindowGranularity,
      clearLinearWindowBounds: state.clearLinearWindowBounds,
      setSynopticTypeId: state.setSynopticTypeId,
      setSynopticZoneKind: state.setSynopticZoneKind,
      setSynopticGranularity: state.setSynopticGranularity,
      toggleSynopticShowPk: state.toggleSynopticShowPk,
      setSynopticTrackIds: state.setSynopticTrackIds,
      setSynopticTrackNetworkId: state.setSynopticTrackNetworkId,
      setSelectedProtectionModeIds: state.setSelectedProtectionModeIds,
      setSelectedActivityTypeIds: state.setSelectedActivityTypeIds,
      setEntryFilter: state.setEntryFilter,
      setSelectedRequestorIds: state.setSelectedRequestorIds,
      setSelectedHolderIds: state.setSelectedHolderIds,
      setSelectedReportOutcomes: state.setSelectedReportOutcomes,
      setReportTodoOnly: state.setReportTodoOnly,
      setSelectedResourceRoleIds: state.setSelectedResourceRoleIds,
      setSelectedResourceLocations: state.setSelectedResourceLocations,
      setListDateMode: state.setListDateMode,
      setListPeriod: state.setListPeriod,
      clearAllFilters: state.clearAllFilters,
    })),
  )
export {useUIStoreBase}
